// Values by key, each in its own reactive cell, for state read per key by
// many views at once (each changed file's diff, read by its row in the
// explorer): a write re-runs what read that key, and nothing else.

class Cell<V> {
  value: V | undefined = $state.raw(undefined);
}

/**
 * A reactive map whose reads follow one key each: `get` and `has` of a key
 * with a cell re-run on writes to that key alone, also while it has no
 * value yet. A key gets its cell from `make`, or from its first `set`; a
 * read of a key without one follows every cell made (as a `SvelteMap` read
 * of a missing key follows every key added), so the keys read often are
 * made ahead, outside any effect: a cell made while an effect or derived
 * runs is not one that run follows (Svelte's rule for state created in a
 * reaction).
 */
export class Cells<V> {
  private cells = new Map<string, Cell<V>>();
  /** Bumped when cells are made: what a read of a key without one follows. */
  private made = $state(0);

  /** Give each of `keys` its cell (with no value) unless it has one. */
  make(keys: Iterable<string>) {
    let grew = false;
    for (const k of keys)
      if (!this.cells.has(k)) {
        this.cells.set(k, new Cell<V>());
        grew = true;
      }
    if (grew) this.made++;
  }

  get(key: string): V | undefined {
    const c = this.cells.get(key);
    if (c) return c.value;
    void this.made;
    return undefined;
  }

  has(key: string): boolean {
    return this.get(key) !== undefined;
  }

  set(key: string, value: V) {
    let c = this.cells.get(key);
    if (!c) {
      this.cells.set(key, (c = new Cell<V>()));
      this.made++;
    }
    c.value = value;
  }

  /** The keys with a value, in the order their cells were made. */
  keys(): string[] {
    void this.made;
    return [...this.cells].filter(([, c]) => c.value !== undefined).map(([k]) => k);
  }
}
